import useUserPreference from '@/utils/hooks/useUserPreference'
import {
	AnimatePresence,
	type HTMLMotionProps,
	type Transition,
	type Variants,
	motion,
} from 'motion/react'
import { useId, useMemo } from 'react'

export enum BlurTextMode {
	SMALL = 'SMALL',
	MEDIUM = 'MEDIUM',
}

export const BLUR_TEXT_VARIANTS_GROUP: Record<BlurTextMode, Variants> = {
	[BlurTextMode.SMALL]: {
		blurred: { filter: 'blur(3px)' },
		clear: { filter: 'blur(0px)' },
	},
	[BlurTextMode.MEDIUM]: {
		blurred: { filter: 'blur(10px)' },
		clear: { filter: 'blur(0px)' },
	},
}
const BLUR_TEXT_TRANSITION_GROUP: Record<BlurTextMode, Transition> = {
	[BlurTextMode.SMALL]: { duration: 0.2, ease: 'easeInOut' },
	[BlurTextMode.MEDIUM]: { duration: 0.3, ease: 'easeInOut' },
}

export interface BlurTextSwitchProps extends HTMLMotionProps<'div'> {
	data: string | number
	mode: BlurTextMode
}

export default function BlurTextSwitch({ data, mode, ...props }: BlurTextSwitchProps) {
	const blurVariants = useMemo(() => BLUR_TEXT_VARIANTS_GROUP[mode], [mode])
	const id = useId()
	return (
		<AnimatePresence mode="wait">
			<motion.div
				key={`blur-text-${data}`}
				layoutId={`blur-text-${id}`}
				initial="blurred"
				animate="clear"
				exit="blurred"
				variants={blurVariants}
				transition={BLUR_TEXT_TRANSITION_GROUP[mode]}
				{...props}
			>
				{data}
			</motion.div>
		</AnimatePresence>
	)
}

export function FeaturableBlurTextSwitch(props: BlurTextSwitchProps) {
	const [isEnabled] = useUserPreference('isActionTextBlurTransitionEffectEnabled')

	if (!isEnabled) return <>{props.data}</>

	return <BlurTextSwitch {...props} />
}
